<template>
  <v-card>
    <v-card-title>All Tournament Structures</v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center pa-4">Loading…</div>

      <div v-else>
        <div v-if="structures.length === 0" class="text-center pa-4">No structures found.</div>

        <div v-for="structure in structures" :key="structure.id" style="margin-bottom: 8px; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
          <!-- Row header -->
          <div style="display: flex; align-items: center; gap: 12px; padding: 12px; flex-wrap: wrap; background: #fafafa;">
            <!-- Name -->
            <span style="min-width: 180px; font-weight: 500;">{{ structure.name }}</span>

            <!-- Owner picker -->
            <v-select
              :model-value="getOwnerAlias(structure)"
              :items="allUsers.map(u => u.alias)"
              label="Owner"
              density="compact"
              hide-details
              style="max-width: 180px;"
              @update:model-value="(alias) => changeOwner(structure, alias)"
            />

            <!-- Global toggle -->
            <v-chip
              :color="structure.isGlobal ? 'green' : 'grey'"
              variant="tonal"
              style="cursor: pointer;"
              @click="toggleFlag(structure, 'isGlobal')"
            >
              {{ structure.isGlobal ? 'Global' : 'Local' }}
            </v-chip>

            <!-- Template toggle -->
            <v-chip
              :color="structure.isTemplate ? 'blue' : 'grey'"
              variant="tonal"
              style="cursor: pointer;"
              @click="toggleFlag(structure, 'isTemplate')"
            >
              {{ structure.isTemplate ? 'Template' : 'Not Template' }}
            </v-chip>

            <!-- Edit toggle -->
            <v-btn
              size="small"
              variant="tonal"
              :color="expandedId === structure.id ? 'primary' : undefined"
              @click="toggleExpand(structure)"
            >
              {{ expandedId === structure.id ? 'Close' : 'Edit' }}
            </v-btn>

            <!-- Delete -->
            <v-btn icon color="error" size="small" @click="requestDelete(structure)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>

          <!-- Inline editor -->
          <v-expand-transition>
            <div v-if="expandedId === structure.id" style="padding: 16px; border-top: 1px solid #ddd;">
              <div style="display: flex; gap: 8px; margin-bottom: 12px;">
                <v-btn color="primary" @click="saveExpanded(structure)">Save Changes</v-btn>
                <v-btn color="grey" variant="text" @click="expandedId = null">Cancel</v-btn>
              </div>
              <div v-if="saveFeedback[structure.id]" style="margin-bottom: 8px; color: green;">
                {{ saveFeedback[structure.id] }}
              </div>
              <StructureEditorForm
                :structure="structure"
                :isAdmin="true"
                @change="(data) => onFormChange(structure.id, data)"
              />
            </div>
          </v-expand-transition>
        </div>
      </div>
    </v-card-text>
  </v-card>

  <!-- Delete confirmation -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title>Delete Structure?</v-card-title>
      <v-card-text>Are you sure you want to delete <strong>{{ toDelete?.name }}</strong>?</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
        <v-btn color="error" text @click="confirmDelete">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, getDocs, doc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { TOURNAMENT_STRUCTURES_DB } from '@/constants/constants';
import StructureEditorForm from '@/components/custom/StructureEditorForm.vue';

const props = defineProps({
  allUsers: { type: Array, default: () => [] }
});

const structures = ref([]);
const loading = ref(false);
const expandedId = ref(null);
const pendingFormData = ref({}); // keyed by structure.id
const saveFeedback = ref({});    // keyed by structure.id
const deleteDialog = ref(false);
const toDelete = ref(null);

async function load() {
  loading.value = true;
  const snapshot = await getDocs(collection(db, TOURNAMENT_STRUCTURES_DB));
  structures.value = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  loading.value = false;
}

function getOwnerAlias(structure) {
  if (!structure.user) return '';
  if (typeof structure.user === 'string') return structure.user;
  return structure.user.alias ?? '';
}

async function changeOwner(structure, alias) {
  const userObj = props.allUsers.find(u => u.alias === alias);
  if (!userObj) return;
  structure.user = userObj;
  await updateDoc(doc(db, TOURNAMENT_STRUCTURES_DB, structure.id), { user: userObj });
}

async function toggleFlag(structure, flag) {
  structure[flag] = !structure[flag];
  await updateDoc(doc(db, TOURNAMENT_STRUCTURES_DB, structure.id), { [flag]: structure[flag] });
}

function toggleExpand(structure) {
  expandedId.value = expandedId.value === structure.id ? null : structure.id;
}

function onFormChange(structureId, data) {
  pendingFormData.value[structureId] = data;
}

async function saveExpanded(structure) {
  const fd = pendingFormData.value[structure.id];
  if (!fd) {
    saveFeedback.value[structure.id] = 'No changes to save.';
    return;
  }
  try {
    const updated = {
      ...fd,
      name:     structure.name,   // keep original name
      user:     structure.user,   // keep current owner
      isGlobal: fd.isGlobal ?? structure.isGlobal,
      isTemplate: fd.isTemplate ?? structure.isTemplate,
    };
    await setDoc(doc(db, TOURNAMENT_STRUCTURES_DB, structure.id), updated);
    // Update local data
    Object.assign(structure, updated);
    saveFeedback.value[structure.id] = 'Saved!';
    setTimeout(() => { saveFeedback.value[structure.id] = ''; }, 3000);
  } catch (e) {
    console.error('Error saving structure:', e);
    saveFeedback.value[structure.id] = 'Error saving.';
  }
}

function requestDelete(structure) {
  toDelete.value = structure;
  deleteDialog.value = true;
}

async function confirmDelete() {
  if (!toDelete.value) return;
  await deleteDoc(doc(db, TOURNAMENT_STRUCTURES_DB, toDelete.value.id));
  structures.value = structures.value.filter(s => s.id !== toDelete.value.id);
  if (expandedId.value === toDelete.value.id) expandedId.value = null;
  deleteDialog.value = false;
  toDelete.value = null;
}

onMounted(load);
</script>
